import type { ModelComparisonResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { STANDARD_DATE_FORMATS, formatDate } from '../../../helpers/date-time-helpers';
import { getTimeAllocationChartLabels } from '../../process-analysis/components/line-chart/line-chart.helpers';
import type { ChartDataItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { EM_DASH, deriveAllocationType } from '../model-comparison/model-comparison.helpers';
import { type UsageMeasure, measureValue } from './usage.type';

/** Compact `MMM D` axis/tooltip formatter, matching the cycle-time chart. */
const formatPeriodLabel = (date: string): string => formatDate(date, { format: STANDARD_DATE_FORMATS.MONTH_DAY });

/**
 * Compact token formatter (e.g. 1.2K, 3.4M) for axis ticks, tooltips, and KPI
 * tiles. Token counts routinely reach millions, so the raw integer is
 * unreadable in a chart axis; `Intl.NumberFormat` compact notation is the
 * standard tool for this (this is number formatting, not date formatting, so
 * it doesn't route through the date-time helpers).
 *
 * @param {number | null | undefined} value - The token count
 * @returns {string} The compact string, or an em-dash for missing data
 */
const formatTokens = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? EM_DASH
    : new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

/**
 * Maps the per-period token series into the shared LineChart data shape: one
 * row per period, keyed by model family, projecting the selected measure out
 * of each period's token bucket. Nulls are preserved so the chart shows gaps
 * for periods without turns.
 *
 * Tick and tooltip labels are delegated to process-analysis's
 * `getTimeAllocationChartLabels` — the same helper the cycle-time chart uses —
 * so the last tick shows the window's inclusive END date rather than the
 * trailing bucket's start. Without this the two charts on the same screen
 * disagree: toggling between them would make the last tick jump, reintroducing
 * the "data stops early" confusion the helper exists to prevent.
 *
 * Each row carries its period's inclusive ISO bounds (`periodStart` /
 * `periodEnd`) for parity with the cycle-time chart; `periodEnd` is the
 * inclusive end so a future dot drill-down's query window matches the label.
 *
 * @param {ModelComparisonResponse} response - The model comparison payload (token series included)
 * @param {UsageMeasure} measure - The selected token measure
 * @returns {ChartDataItem[]} One chart row per period
 */
const buildTokenChartData = (response: ModelComparisonResponse, measure: UsageMeasure): ChartDataItem[] => {
  const series = response.token_usage_series ?? [];
  const timeAllocationType =
    response.meta.time_allocation_type ?? deriveAllocationType(response.meta.start_date, response.meta.end_date);

  return response.meta.periods.map((period) => {
    const { tickLabel, tooltipLabel, startDate, endDate } = getTimeAllocationChartLabels({
      rawDate: period.id,
      timeAllocationType,
      timeAllocations: response.meta.periods,
      formatDateLabel: formatPeriodLabel,
    });
    const row: ChartDataItem = {
      date: tickLabel,
      rawDate: period.id,
      dateRange: tooltipLabel,
      // Bounds from the label helper — one timezone-independent derivation
      // shared with the axis label (parity with the cycle-time chart).
      periodStart: startDate,
      periodEnd: endDate,
    };
    for (const entry of series) {
      const bucket = entry.values[period.id];
      row[entry.model_family] = bucket ? measureValue(bucket, measure) : null;
    }
    return row;
  });
};

export { buildTokenChartData, formatTokens };
